import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { PRODUCT_NAME } from '@learnwithai/ui-shared';
import { parentApi, storeRefreshToken, type ApiError } from '../api';

// ── Live invite-code validation (Codex #3) ───────────────────────────────────
// "Verified ✓" used to appear on client-side length alone — a lie when the code
// was fake/used. Now a full-length code is checked against the SERVER
// (POST /auth/validate-invite, debounced) and "verified" renders ONLY on
// valid:true. Submit-time enforcement in /auth/register is unchanged.
//   idle      — code not yet full length
//   checking  — full length, server validation in flight
//   valid     — server said valid:true
//   invalid   — server said valid:false (reason shown)
//   unknown   — validation call itself failed (network/rate-limit): no green
//               checkmark, but submission stays possible — the register
//               endpoint re-validates authoritatively anyway.
export type InviteCheckState = 'idle' | 'checking' | 'valid' | 'invalid' | 'unknown';

/** Maps the reasons /auth/validate-invite emits to parent-friendly copy. */
export function inviteReasonCopy(reason: string | undefined, fr: boolean): string {
  switch (reason) {
    case 'Already used':
      return fr
        ? 'Ce code d’invitation a déjà été utilisé. Chaque code ne fonctionne qu’une seule fois.'
        : 'This invitation code has already been used. Each code works only once.';
    case 'Code not found':
      return fr
        ? 'Ce code d’invitation est introuvable. Vérifiez chaque caractère et réessayez.'
        : 'We couldn’t find this invitation code. Double-check each character and try again.';
    case 'Invalid format':
      return fr
        ? 'Le code doit comporter exactement 20 caractères (lettres et chiffres).'
        : 'The code must be exactly 20 characters (letters and numbers).';
    default:
      return fr
        ? 'Ce code d’invitation n’est pas valide.'
        : 'This invitation code isn’t valid.';
  }
}

const INVITE_CHECK_DEBOUNCE_MS = 400;

/** Brand wordmark header — identical treatment to LoginPage so the funnel reads
 *  as one product (dark-indigo gradient badge + ReadWithAI wordmark). */
function BrandLogo() {
  return (
    <div style={{ marginBottom: 32, textAlign: 'center', animation: 'lpFadeIn 0.4s ease' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#5B4FE8,#FF6B50)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 900, fontSize: 22 }}>{PRODUCT_NAME.charAt(0)}</div>
        <span style={{ fontWeight: 900, fontSize: '1.4rem', color: '#fff', letterSpacing: '-0.02em' }}>{PRODUCT_NAME}</span>
      </div>
    </div>
  );
}

export default function RegisterPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [parentalConsent, setParentalConsent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteState, setInviteState] = useState<InviteCheckState>('idle');
  const [inviteReason, setInviteReason] = useState<string | undefined>(undefined);
  // Open signup (owner-locked 2026-08-30): the invite/school code is an OPTIONAL
  // path. The code UI starts COLLAPSED unless ?code= arrived (school/tester
  // links) or the server demands one (staged `Enrollment:InviteRequired` window).
  const [showCode, setShowCode] = useState(false);
  // Guards against out-of-order validate responses after quick edits.
  const inviteCheckSeq = useRef(0);
  // After a successful register we show a "check your email" confirmation (the
  // parent is signed in, but must verify their email before a child can start a
  // lesson — the soft gate on /auth/child-session).
  const [registered, setRegistered] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (!codeFromUrl) return; // no code → open signup, code UI stays collapsed
    const normalized = codeFromUrl.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 20);
    setInviteCode(normalized);
    setShowCode(true);
  }, [searchParams]);

  // Debounced SERVER validation whenever a full-length code is present
  // (typed, pasted, or arriving via the ?code= URL).
  useEffect(() => {
    if (inviteCode.length !== 20) {
      setInviteState('idle');
      setInviteReason(undefined);
      return;
    }
    const seq = ++inviteCheckSeq.current;
    setInviteState('checking');
    setInviteReason(undefined);
    const timer = window.setTimeout(() => {
      parentApi.validateInvite(inviteCode)
        .then((res) => {
          if (seq !== inviteCheckSeq.current) return; // stale response
          if (res.valid) {
            setInviteState('valid');
            sessionStorage.setItem('invite_code', inviteCode);
          } else {
            setInviteState('invalid');
            setInviteReason(res.reason);
          }
        })
        .catch(() => {
          if (seq !== inviteCheckSeq.current) return;
          // Couldn't reach the validator (offline / rate-limited): stay honest —
          // no green checkmark — but let register's own server check decide.
          setInviteState('unknown');
        });
    }, INVITE_CHECK_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [inviteCode]);

  const handleInviteCodeChange = (value: string) => {
    const normalized = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 20);
    setInviteCode(normalized);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 12) {
      setError(t('auth.passwordTooShort', 'Password must be at least 12 characters'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch', 'Passwords do not match.'));
      return;
    }
    if (!consent) {
      setError(t('auth.consentRequired', 'Please accept the Terms and Privacy Policy to continue.'));
      return;
    }
    if (!parentalConsent) {
      setError(
        i18n.language.startsWith('fr')
          ? 'Veuillez confirmer que vous êtes le parent ou tuteur légal de l\'enfant.'
          : 'Please confirm you are the parent or legal guardian of the child who will use this account.'
      );
      return;
    }

    // The code is OPTIONAL — but if the parent typed one, it must be complete
    // and not server-rejected before we submit with it.
    if (inviteCode.length > 0 && (inviteCode.length !== 20 || inviteState === 'invalid')) {
      setError(
        inviteState === 'invalid'
          ? inviteReasonCopy(inviteReason, i18n.language.startsWith('fr'))
          : i18n.language.startsWith('fr')
            ? 'Le code doit comporter 20 caractères — ou videz le champ pour vous inscrire sans code.'
            : 'The code must be 20 characters — or clear the field to sign up without one.'
      );
      return;
    }

    setLoading(true);
    try {
      const locale = i18n.language.startsWith('fr') ? 'fr-FR' : 'en-US';
      const res = await parentApi.register(email, password, locale, inviteCode || undefined, true, parentalConsent);
      sessionStorage.removeItem('invite_code');
      if (res.token) {
        localStorage.setItem('auth_token', res.token);
        storeRefreshToken(res.refreshToken);
      }
      // Stamp the local "unverified" flag so the dashboard shows a persistent,
      // dismissible verify banner (P1-18) — the parent can browse the portal now
      // and verify before starting a lesson (VerifyEmailPage clears this flag).
      try { localStorage.setItem('lwa_email_unverified_v1', email); } catch { /* ignore */ }
      // Show the "check your email" confirmation regardless — verification is the
      // gate to launching a child session, even though login itself isn't blocked.
      setRegistered(true);
    } catch (err: unknown) {
      // Scale P0.7 enrollment geo-fence: the server refuses clearly-out-of-region
      // signups with this stable code — render the kind bilingual explanation
      // (from the parent catalogs), never the raw English server text.
      if ((err as ApiError)?.code === 'enrollment-region-unsupported') {
        setError(t(
          'auth.regionUnsupported',
          `${PRODUCT_NAME} is currently available in the United States and Canada; we can’t create new accounts from other regions just yet. Thank you for your interest — we hope to welcome your family soon!`
        ));
      } else if ((err as ApiError)?.code === 'invite-required') {
        // Staged-rollout window: until `Enrollment:InviteRequired` is switched
        // off in prod config, the server may still insist on an invitation.
        // Expand the code section and explain kindly — no dead-end.
        setShowCode(true);
        setError(
          i18n.language.startsWith('fr')
            ? 'Pour le moment, un code d’invitation est nécessaire pour créer un compte. Si vous en avez un, saisissez-le ci-dessus et réessayez.'
            : 'For now, an invitation code is needed to create an account. If you have one, enter it above and try again.'
        );
      } else {
        setError(err instanceof Error ? err.message : t('auth.registerError', 'Registration failed. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const isFr = i18n.language.startsWith('fr');

  if (registered) {
    return (
      <div style={styles.container}>
        <style>{brandStyle}</style>
      {/* Always-visible language toggle: every INITIAL page must let a family
          switch EN↔FR before doing anything else (FR-tester feedback 2026-09-06). */}
      <button data-testid="lang-toggle" onClick={() => void i18n.changeLanguage(isFr ? 'en-US' : 'fr-FR')}
        style={{ position:'fixed', top:16, right:16, zIndex:50, background:'none', border:'1px solid rgba(255,255,255,0.35)', borderRadius:8, padding:'5px 12px', fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.85)', cursor:'pointer', whiteSpace:'nowrap' }}>
        {isFr ? '🇬🇧 EN' : '🇫🇷 FR'}
      </button>
        <BrandLogo />
        <div style={styles.card}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>📬</div>
            <h1 style={styles.title}>{isFr ? 'Vérifiez votre courriel' : 'Check your email'}</h1>
            <p style={{ color: 'rgba(255,255,255,0.70)', lineHeight: 1.55, marginBottom: 20 }}>
              {isFr
                ? <>Nous avons envoyé un lien de vérification à <strong style={{ color: '#fff' }}>{email}</strong>. Vous pouvez déjà explorer votre tableau de bord et ajouter un enfant — confirmez simplement votre adresse avant de lancer la première leçon. Vous retrouverez ce rappel dans le tableau de bord.</>
                : <>We sent a verification link to <strong style={{ color: '#fff' }}>{email}</strong>. You can explore your dashboard and add a child right away — just confirm your address before starting the first lesson. You'll find this reminder in your dashboard too.</>}
            </p>
            {resent && (
              <p style={{ color: '#3ECFBD', fontSize: '0.88rem', marginBottom: 12 }}>
                {isFr ? 'Nouveau lien envoyé.' : 'A new link is on the way.'}
              </p>
            )}
            <button type="button" className="lp-btn-primary" style={{ marginBottom: 10 }} onClick={() => navigate('/dashboard')}>
              {isFr ? 'Continuer vers le tableau de bord' : 'Continue to dashboard'}
            </button>
            <button
              type="button"
              onClick={async () => { try { await parentApi.resendVerification(email); setResent(true); } catch { setResent(true); } }}
              style={{ background: 'none', border: 'none', color: '#7C6FF0', cursor: 'pointer', fontSize: '0.9rem' }}
            >
              {isFr ? 'Renvoyer le courriel de vérification' : 'Resend verification email'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <style>{brandStyle}</style>
      {/* Always-visible language toggle: every INITIAL page must let a family
          switch EN↔FR before doing anything else (FR-tester feedback 2026-09-06). */}
      <button data-testid="lang-toggle" onClick={() => void i18n.changeLanguage(isFr ? 'en-US' : 'fr-FR')}
        style={{ position:'fixed', top:16, right:16, zIndex:50, background:'none', border:'1px solid rgba(255,255,255,0.35)', borderRadius:8, padding:'5px 12px', fontSize:13, fontWeight:700, color:'rgba(255,255,255,0.85)', cursor:'pointer', whiteSpace:'nowrap' }}>
        {isFr ? '🇬🇧 EN' : '🇫🇷 FR'}
      </button>
      <BrandLogo />
      <div style={styles.card}>
        <h1 style={styles.title}>{t('auth.register')}</h1>
        <h2 style={styles.subtitle}>{t('app.title')}</h2>

        <form onSubmit={handleRegister} style={styles.form}>
          <label style={styles.label}>
            {t('auth.email')}
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={254} className="lp-input" autoComplete="email" />
          </label>

          <label style={styles.label}>
            {t('auth.password')}
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="lp-input" minLength={12} maxLength={128} autoComplete="new-password" />
          </label>

          <label style={styles.label}>
            {t('auth.confirmPassword')}
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="lp-input" autoComplete="new-password" />
          </label>

          {/* Optional invite/school code — collapsed unless ?code= arrived or the
              parent asks for it (codes remain a supported path, never required
              client-side). */}
          {!showCode ? (
            <button
              type="button"
              onClick={() => setShowCode(true)}
              style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', color: '#7C6FF0', cursor: 'pointer', fontSize: '0.9rem', textDecoration: 'underline', textUnderlineOffset: 3 }}
            >
              {isFr ? 'Vous avez un code d’invitation ou un code d’école ?' : 'Have an invite or school code?'}
            </button>
          ) : (
            <label style={styles.label}>
              {isFr ? 'Code d’invitation ou d’école (facultatif)' : 'Invite or school code (optional)'}
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => handleInviteCodeChange(e.target.value)}
                className="lp-input"
                inputMode="text"
                autoComplete="one-time-code"
                placeholder="ABCDE-12345-FGHIJ-67890"
              />
            </label>
          )}

          {/* COPPA parental consent section */}
          <div style={styles.coppaBox}>
            <p style={styles.coppaHeading}>
              {i18n.language.startsWith('fr')
                ? '🔒 Consentement parental requis (COPPA / RGPD)'
                : '🔒 Parental Consent Required (COPPA / GDPR)'}
            </p>
            <p style={styles.coppaBody}>
              {i18n.language.startsWith('fr')
                ? `${PRODUCT_NAME} est destiné aux enfants de 5 à 13 ans. La création d'un compte nécessite le consentement d'un parent ou tuteur légal âgé d'au moins 18 ans.`
                : `${PRODUCT_NAME} is designed for children ages 5–13. Creating an account requires consent from a parent or legal guardian aged 18 or older.`}
            </p>

            <label style={styles.checkLabel}>
              <input
                type="checkbox"
                checked={parentalConsent}
                onChange={(e) => setParentalConsent(e.target.checked)}
                style={{ width: 16, height: 16, marginTop: 2, accentColor: '#5B4FE8', flexShrink: 0 }}
              />
              <span style={{ fontSize: '0.88rem', lineHeight: 1.5 }}>
                {i18n.language.startsWith('fr')
                  ? 'Je confirme que j\'ai au moins 18 ans et que je suis le parent ou tuteur légal de l\'enfant qui utilisera ce compte.'
                  : 'I confirm that I am at least 18 years old and the parent or legal guardian of the child who will use this account.'}
              </span>
            </label>

            <label style={{ ...styles.checkLabel, marginTop: '8px' }}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                style={{ width: 16, height: 16, marginTop: 2, accentColor: '#5B4FE8', flexShrink: 0 }}
              />
              <span style={{ fontSize: '0.88rem', lineHeight: 1.5 }}>
                {i18n.language.startsWith('fr') ? (
                  <>{`J'accepte les `}<Link to="/terms" target="_blank" style={{ color: '#7C6FF0' }}>{`Conditions d'utilisation`}</Link>{` et la `}<Link to="/privacy" target="_blank" style={{ color: '#7C6FF0' }}>{`Politique de confidentialité`}</Link>{`. Je comprends que mes données et celles de mon enfant seront utilisées uniquement pour fournir le service de tutorat.`}</>
                ) : (
                  <>{`I agree to the `}<Link to="/terms" target="_blank" style={{ color: '#7C6FF0' }}>{`Terms of Service`}</Link>{` and `}<Link to="/privacy" target="_blank" style={{ color: '#7C6FF0' }}>{`Privacy Policy`}</Link>{`. I understand that my data and my child's data will be used only to provide the tutoring service.`}</>
                )}
              </span>
            </label>
          </div>

          {inviteState === 'checking' && (
            <div style={{ background: 'rgba(91,79,232,0.12)', border: '1px solid rgba(91,79,232,0.35)', borderRadius: 10, padding: '10px 14px', fontSize: '0.85rem', color: '#C4B5FD', display: 'flex', alignItems: 'center', gap: 8 }} role="status">
              <span aria-hidden="true" style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(196,181,253,0.4)', borderTopColor: '#C4B5FD', borderRadius: '50%', animation: 'lwaInviteSpin 0.8s linear infinite' }} />
              <span>{i18n.language.startsWith('fr') ? 'Vérification du code…' : 'Checking your code…'}</span>
              <style>{'@keyframes lwaInviteSpin { to { transform: rotate(360deg); } }'}</style>
            </div>
          )}

          {/* "Verified" renders ONLY after the SERVER confirms valid:true (Codex #3). */}
          {inviteState === 'valid' && (
            <div style={{ background: 'rgba(62,207,189,0.12)', border: '1px solid rgba(62,207,189,0.40)', borderRadius: 10, padding: '10px 14px', fontSize: '0.85rem', color: '#5EEAD4', display: 'flex', alignItems: 'center', gap: 8 }} role="status">
              <span>✓</span>
              <span>{t('auth.inviteVerified', 'Invitation code verified:')} <strong>{inviteCode.slice(0,5)}-{inviteCode.slice(5,10)}-{inviteCode.slice(10,15)}-{inviteCode.slice(15,20)}</strong></span>
            </div>
          )}

          {inviteState === 'invalid' && (
            <div style={{ background: 'rgba(255,107,80,0.12)', border: '1px solid rgba(255,107,80,0.35)', borderRadius: 10, padding: '10px 14px', fontSize: '0.85rem', color: '#FF9B85', display: 'flex', alignItems: 'flex-start', gap: 8 }} role="alert">
              <span>✕</span>
              <span>{inviteReasonCopy(inviteReason, i18n.language.startsWith('fr'))}</span>
            </div>
          )}

          {error && <p style={styles.error}>{error}</p>}

          <button
            type="submit"
            className="lp-btn-primary"
            disabled={loading || (inviteCode.length > 0 && (inviteCode.length !== 20 || inviteState === 'invalid' || inviteState === 'checking'))}
            style={{ marginTop: 6 }}
          >
            {loading ? t('common.loading') : t('auth.register')}
          </button>
        </form>

        <p style={styles.link}>
          {t('auth.hasAccount')}{' '}
          <a href="/login" style={{ color: '#7C6FF0' }}>{t('auth.login')}</a>
        </p>
      </div>
    </div>
  );
}

// Shared dark-indigo brand palette — identical to LoginPage (P) so /register and
// /login read as one polished funnel (BUG: RegisterPage was an off-brand light
// form). Kept as its own consts to mirror LoginPage's approach exactly.
const P = { indigo: '#5B4FE8', coral: '#FF6B50', dark: '#1A1035', text: '#18181B', sub: '#6B7280', border: '#E5E7EB', bg: '#FAFAFA' };

// The `.lp-input` / `.lp-btn-primary` classes are the same gradient/glass
// treatment LoginPage uses; `.lp-link` is the indigo inline-link accent.
const brandStyle = `
  .lp-input { background:rgba(255,255,255,0.07)!important; border:1px solid rgba(255,255,255,0.15)!important; color:#fff!important; border-radius:12px!important; padding:14px 16px!important; font-size:1rem!important; width:100%; outline:none; transition:border 0.2s; }
  .lp-input::placeholder { color:rgba(255,255,255,0.35); }
  .lp-input:focus { border-color:#5B4FE8!important; box-shadow:0 0 0 3px rgba(91,79,232,0.25); }
  .lp-btn-primary { width:100%; padding:14px; border:none; border-radius:100px; background:linear-gradient(135deg,#5B4FE8 0%,#7C3AED 60%,#FF6B50 100%); background-size:200% auto; color:#fff; font-weight:800; font-size:1rem; cursor:pointer; box-shadow:0 8px 32px rgba(91,79,232,0.40); transition:box-shadow 0.2s,transform 0.2s; }
  .lp-btn-primary:hover:not(:disabled) { box-shadow:0 12px 40px rgba(91,79,232,0.55); transform:translateY(-1px); }
  .lp-btn-primary:disabled { opacity:0.6; cursor:not-allowed; }
  .lp-link { color:#7C6FF0; text-decoration:none; font-weight:600; }
  @keyframes lpFadeIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
`;

const styles: Record<string, React.CSSProperties> = {
  container: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: P.dark, padding: '24px' },
  card: { width: '100%', maxWidth: '440px', background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '24px', padding: '40px', animation: 'lpFadeIn 0.45s ease' },
  title: { textAlign: 'center', color: '#fff', fontWeight: 800, fontSize: '1.5rem', letterSpacing: '-0.02em', margin: '0 0 6px' },
  subtitle: { textAlign: 'center', color: 'rgba(255,255,255,0.55)', fontWeight: 400, fontSize: '0.9rem', margin: '0 0 24px' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  label: { display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.04em' },
  checkLabel: { display: 'flex', gap: '8px', alignItems: 'flex-start', color: 'rgba(255,255,255,0.75)' },
  coppaBox: { background: 'rgba(91,79,232,0.12)', border: '1px solid rgba(91,79,232,0.30)', borderRadius: '14px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  coppaHeading: { margin: 0, fontSize: '0.88rem', fontWeight: 700, color: '#C4B5FD' },
  coppaBody: { margin: 0, fontSize: '0.82rem', color: 'rgba(255,255,255,0.70)', lineHeight: 1.55 },
  error: { color: '#FF6B50', fontSize: '0.88rem', background: 'rgba(255,107,80,0.12)', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(255,107,80,0.25)' },
  link: { textAlign: 'center', marginTop: '16px', color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem' },
};
